import React, { useEffect, useState } from "react";
import Back from "./common/Back";
import { useNavigate } from "react-router-dom";
import { apiUrl } from "../config/config";
import StripeCheckout from "./StripeCheckout";
import SEO from "./SEO";
import { brands } from "../config/config";

// --------------------
// USER / GUEST ID HELPERS
// --------------------
const getUserOrGuestId = () => {
  const user = localStorage.getItem("user");

  if (user) {
    try {
      const parsed = JSON.parse(user);
      if (parsed?.id) return { user_id: parsed.id, guest_id: null };
    } catch {}
  }

  let guest_id = localStorage.getItem("guest_id");
  if (!guest_id) {
    guest_id = "guest_" + Math.random().toString(36).substring(2, 12);
    localStorage.setItem("guest_id", guest_id);
  }

  return { user_id: null, guest_id };
};

const calculateDeliveryDate = (daysToAdd) => {
  const date = new Date();
  date.setDate(date.getDate() + daysToAdd);
  return date.toISOString().split("T")[0];
};

const CheckoutSkeleton = () => {
  const inputRows = Array.from({ length: 8 });
  const summaryRows = Array.from({ length: 4 });

  return (
    <div>
      <Back title="Checkout" />

      <div className="min-h-screen bg-lightPastelPurple px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl animate-pulse">
          <div className="relative mb-10 overflow-hidden rounded-[34px] border border-pastelPurple bg-white px-6 py-10 shadow-[0_22px_60px_rgba(47,34,84,0.10)] md:px-10">
            <div className="absolute -left-24 -top-24 h-80 w-80 rounded-full bg-pastelPurple/70 blur-3xl"></div>
            <div className="absolute -right-24 -bottom-24 h-80 w-80 rounded-full bg-keyLime/25 blur-3xl"></div>

            <div className="relative">
              <div className="mx-auto h-10 w-56 rounded-xl bg-pastelPurple/60"></div>
              <div className="mx-auto mt-5 h-3 w-48 rounded-full bg-keyLime/60"></div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.25fr_0.75fr]">
            <div className="space-y-8">
              <div className="relative overflow-hidden rounded-[30px] border border-pastelPurple bg-white p-6 shadow-[0_18px_45px_rgba(47,34,84,0.08)] md:p-8">
                <div className="absolute left-0 top-0 h-72 w-72 rounded-full bg-pastelPurple/60 blur-3xl"></div>
                <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-keyLime/20 blur-3xl"></div>

                <div className="relative">
                  <div className="h-8 w-48 rounded-xl bg-pastelPurple/60"></div>
                  <div className="mt-4 h-2 w-28 rounded-full bg-keyLime/60"></div>

                  <div className="mt-7 grid grid-cols-1 gap-6 md:grid-cols-2">
                    {inputRows.map((_, index) => (
                      <div
                        key={index}
                        className={
                          index === 2 || index === 7 ? "md:col-span-2" : ""
                        }
                      >
                        <div className="h-4 w-28 rounded bg-pastelPurple/60"></div>
                        <div className="mt-3 h-12 rounded-2xl bg-lightPastelPurple"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="relative overflow-hidden rounded-[30px] border border-pastelPurple bg-white p-6 shadow-[0_18px_45px_rgba(47,34,84,0.08)] md:p-8">
                <div className="h-14 rounded-2xl bg-lightPastelPurple"></div>
              </div>

              <div className="h-36 rounded-[26px] border border-pastelPurple bg-white shadow-[0_18px_45px_rgba(47,34,84,0.08)]"></div>
            </div>

            <div className="lg:sticky lg:top-24 lg:self-start">
              <div className="overflow-hidden rounded-[30px] border border-pastelPurple bg-white shadow-[0_18px_45px_rgba(47,34,84,0.08)]">
                <div className="bg-darkPurple px-6 py-5">
                  <div className="h-7 w-44 rounded-xl bg-white/20"></div>
                </div>

                <div className="space-y-4 p-6">
                  {summaryRows.map((_, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <div className="h-14 w-14 rounded-2xl bg-lightPastelPurple"></div>
                      <div className="flex-1 space-y-3">
                        <div className="h-4 w-3/4 rounded bg-pastelPurple/60"></div>
                        <div className="h-4 w-1/2 rounded bg-lightPastelPurple"></div>
                      </div>
                    </div>
                  ))}

                  <div className="mt-6 h-12 rounded-2xl bg-lightPastelPurple"></div>
                  <div className="h-12 rounded-2xl bg-keyLime/60"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Checkout = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [countries, setCountries] = useState([]);
  const [errors, setErrors] = useState({});
  const [zip, setZip] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");

  const [isShippingSame, setIsShippingSame] = useState(true);
  const [shippingFirstName, setShippingFirstName] = useState("");
  const [shippingLastName, setShippingLastName] = useState("");
  const [shippingAddress, setShippingAddress] = useState("");
  const [shippingCity, setShippingCity] = useState("");
  const [shippingCountry, setShippingCountry] = useState("");
  const [shippingZip, setShippingZip] = useState("");
  const [shippingErrors, setShippingErrors] = useState({});

  const [isRushDelivery, setIsRushDelivery] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState(calculateDeliveryDate(14));
  const [shippingCharge, setShippingCharge] = useState(20);

  const navigate = useNavigate();
  const subtotal = cartItems.reduce((sum, item) => sum + Number(item.price), 0);
  const total = subtotal + shippingCharge;

  useEffect(() => {
    fetch("https://backend.inkox.com/api/countries")
      .then((res) => res.json())
      .then((data) => setCountries(data))
      .catch((err) => console.error("Country fetch error:", err));
  }, []);

  useEffect(() => {
    if (isRushDelivery) {
      setDeliveryDate(calculateDeliveryDate(7));
      setShippingCharge(20 + subtotal * 0.2);
    } else {
      setDeliveryDate(calculateDeliveryDate(14));
      setShippingCharge(20);
    }
  }, [isRushDelivery, subtotal]);

  useEffect(() => {
    if (isShippingSame) {
      setShippingFirstName(firstName);
      setShippingLastName(lastName);
      setShippingAddress(address);
      setShippingCity(city);
      setShippingCountry(country);
      setShippingZip(zip);
    }
  }, [isShippingSame, firstName, lastName, address, city, country, zip]);

  useEffect(() => {
    const { user_id, guest_id } = getUserOrGuestId();

    const url = user_id
      ? `${apiUrl}/api/cart/user/${user_id}`
      : `${apiUrl}/api/cart?guest_id=${guest_id}`;

    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setCartItems(data.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log("Checkout fetch error:", err);
        setLoading(false);
      });
  }, []);

  const inputClass = (hasError) =>
    `w-full mt-2 px-5 py-3.5 rounded-2xl border bg-lightPastelPurple text-darkPurple font-medium outline-none transition-all duration-200 ${
      hasError
        ? "border-red-500 focus:ring-4 focus:ring-red-100"
        : "border-pastelPurple focus:border-themePurple focus:bg-white focus:ring-4 focus:ring-pastelPurple/60"
    }`;

  const labelClass = "text-sm font-extrabold text-darkPurple";
  const errorClass = "text-red-500 text-sm mt-1 font-medium";

  const validateBillingForm = () => {
    const newErrors = {};

    if (!firstName) newErrors.firstName = true;
    if (!lastName) newErrors.lastName = true;
    if (!address) newErrors.address = true;
    if (!city) newErrors.city = true;
    if (!country) newErrors.country = true;
    if (!zip) newErrors.zip = true;
    if (!mobile) newErrors.mobile = true;
    if (!email) newErrors.email = true;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateShippingForm = () => {
    if (isShippingSame) return true;

    const newErrors = {};

    if (!shippingFirstName) newErrors.shippingFirstName = true;
    if (!shippingLastName) newErrors.shippingLastName = true;
    if (!shippingAddress) newErrors.shippingAddress = true;
    if (!shippingCity) newErrors.shippingCity = true;
    if (!shippingCountry) newErrors.shippingCountry = true;
    if (!shippingZip) newErrors.shippingZip = true;

    setShippingErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateAllForms = () => {
    const isBillingValid = validateBillingForm();
    const isShippingValid = validateShippingForm();
    return isBillingValid && isShippingValid;
  };

  const placeOrder = async (payment_id, paymentStatus = "paid") => {
    const { user_id, guest_id } = getUserOrGuestId();

    const shippingAddressObj = {
      first_name: shippingFirstName,
      last_name: shippingLastName,
      address: shippingAddress,
      city: shippingCity,
      country: shippingCountry,
      zip: shippingZip,
      is_same_as_billing: isShippingSame,
    };

    const payload = {
      user_id,
      guest_id,
      first_name: firstName,
      last_name: lastName,
      address,
      city,
      country,
      zip,
      mobile,
      email,
      notes,
      subtotal,
      shipping: shippingCharge,
      total,
      payment_method: "stripe",
      payment_id,
      payment_status: paymentStatus,
      brand: brands,
      cart_items: cartItems,
      shipping_address: shippingAddressObj,
      delivery_date: {
        date: deliveryDate,
        is_rush_delivery: isRushDelivery,
      },
    };

    const res = await fetch(`${apiUrl}/api/checkout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const data = await res.json();

    if (data.success) {
      navigate("/thank-you", { state: { orderId: data.order_id } });
    }
  };

  if (loading) {
    return <CheckoutSkeleton />;
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-lightPastelPurple">
        <Back title="Checkout" />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center">
          <div className="relative overflow-hidden rounded-[34px] border border-pastelPurple bg-white px-6 py-14 shadow-[0_22px_60px_rgba(47,34,84,0.10)]">
            <div className="absolute -left-24 -top-24 h-80 w-80 rounded-full bg-pastelPurple/70 blur-3xl"></div>
            <div className="absolute -right-24 -bottom-24 h-80 w-80 rounded-full bg-keyLime/25 blur-3xl"></div>

            <div className="relative">
              <h2 className="text-3xl font-extrabold text-darkPurple md:text-4xl">
                Your cart is empty
              </h2>

              <a
                href="/"
                className="mt-7 inline-flex rounded-2xl bg-keyLime px-7 py-3.5 text-sm font-extrabold text-darkPurple shadow-[0_12px_26px_rgba(215,255,79,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#c8f531]"
              >
                Continue Shopping
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Back title="Checkout" />
      <SEO
        title="Checkout"
        description="Personalize Patches For Hats"
        keywords="Personalize Patches For Hats"
      />

      <div className="min-h-screen bg-lightPastelPurple px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="relative mb-10 overflow-hidden rounded-[34px] border border-pastelPurple bg-white px-6 py-10 text-center shadow-[0_22px_60px_rgba(47,34,84,0.10)] md:px-10">
            <div className="absolute -left-24 -top-24 h-80 w-80 rounded-full bg-pastelPurple/70 blur-3xl"></div>
            <div className="absolute -right-24 -bottom-24 h-80 w-80 rounded-full bg-keyLime/25 blur-3xl"></div>

            <div className="relative">
              <h2 className="text-4xl font-extrabold leading-tight tracking-tight text-darkPurple md:text-5xl">
                Checkout
              </h2>

              <div className="mx-auto mt-4 flex w-full max-w-[230px] items-center justify-center gap-3">
                <span className="h-[1px] flex-1 bg-[#d8c8ff]"></span>
                <span className="h-2.5 w-2.5 rounded-full bg-keyLime shadow-[0_0_0_5px_rgba(215,255,79,0.25)]"></span>
                <span className="h-[1px] flex-1 bg-[#d8c8ff]"></span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.25fr_0.75fr]">
            <div className="space-y-8">
              <div className="relative overflow-hidden rounded-[30px] border border-pastelPurple bg-white p-6 shadow-[0_18px_45px_rgba(47,34,84,0.08)] md:p-8">
                <div className="absolute left-0 top-0 h-72 w-72 rounded-full bg-pastelPurple/60 blur-3xl"></div>
                <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-keyLime/20 blur-3xl"></div>

                <div className="relative">
                  <h3 className="text-2xl font-extrabold text-darkPurple">
                    Billing details
                  </h3>

                  <div className="mt-4 flex items-center gap-3">
                    <span className="h-1 w-16 rounded-full bg-keyLime"></span>
                    <span className="h-1 w-8 rounded-full bg-pastelPurple"></span>
                  </div>

                  <form className="mt-7 space-y-6">
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div>
                        <label className={labelClass}>
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          className={inputClass(errors.firstName)}
                          placeholder="Enter your first name"
                          value={firstName}
                          onChange={(e) => {
                            setFirstName(e.target.value);
                            setErrors({ ...errors, firstName: false });
                          }}
                        />
                        {errors.firstName && (
                          <p className={errorClass}>First name is required</p>
                        )}
                      </div>

                      <div>
                        <label className={labelClass}>
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          className={inputClass(errors.lastName)}
                          placeholder="Enter your last name"
                          value={lastName}
                          onChange={(e) => {
                            setLastName(e.target.value);
                            setErrors({ ...errors, lastName: false });
                          }}
                        />
                        {errors.lastName && (
                          <p className={errorClass}>Last name is required</p>
                        )}
                      </div>

                      <div className="md:col-span-2">
                        <label className={labelClass}>
                          Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          className={inputClass(errors.address)}
                          placeholder="Enter your address"
                          value={address}
                          onChange={(e) => {
                            setAddress(e.target.value);
                            setErrors({ ...errors, address: false });
                          }}
                        />
                        {errors.address && (
                          <p className={errorClass}>Address is required</p>
                        )}
                      </div>

                      <div>
                        <label className={labelClass}>
                          Town / City <span className="text-red-500">*</span>
                        </label>
                        <input
                          className={inputClass(errors.city)}
                          placeholder="Enter your city"
                          value={city}
                          onChange={(e) => {
                            setCity(e.target.value);
                            setErrors({ ...errors, city: false });
                          }}
                        />
                        {errors.city && (
                          <p className={errorClass}>City is required</p>
                        )}
                      </div>

                      <div>
                        <label className={labelClass}>
                          Country <span className="text-red-500">*</span>
                        </label>
                        <select
                          className={inputClass(errors.country)}
                          value={country}
                          onChange={(e) => {
                            setCountry(e.target.value);
                            setErrors({ ...errors, country: false });
                          }}
                        >
                          <option value="">Select a country</option>
                          {countries.map((c) => (
                            <option key={c.code} value={c.name}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                        {errors.country && (
                          <p className={errorClass}>Country is required</p>
                        )}
                      </div>

                      <div>
                        <label className={labelClass}>
                          Postcode / ZIP{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          className={inputClass(errors.zip)}
                          placeholder="Enter ZIP"
                          value={zip}
                          onChange={(e) => {
                            setZip(e.target.value);
                            setErrors({ ...errors, zip: false });
                          }}
                        />
                        {errors.zip && (
                          <p className={errorClass}>ZIP is required</p>
                        )}
                      </div>

                      <div>
                        <label className={labelClass}>
                          Mobile <span className="text-red-500">*</span>
                        </label>
                        <input
                          className={inputClass(errors.mobile)}
                          placeholder="Enter mobile number"
                          value={mobile}
                          onChange={(e) => {
                            setMobile(e.target.value);
                            setErrors({ ...errors, mobile: false });
                          }}
                        />
                        {errors.mobile && (
                          <p className={errorClass}>
                            Mobile number is required
                          </p>
                        )}
                      </div>

                      <div className="md:col-span-2">
                        <label className={labelClass}>
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          className={inputClass(errors.email)}
                          placeholder="Enter your email"
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            setErrors({ ...errors, email: false });
                          }}
                        />
                        {errors.email && (
                          <p className={errorClass}>Email is required</p>
                        )}
                      </div>
                    </div>
                  </form>
                </div>
              </div>

              <div className="relative overflow-hidden rounded-[30px] border border-pastelPurple bg-white p-6 shadow-[0_18px_45px_rgba(47,34,84,0.08)] md:p-8">
                <div className="relative">
                  <label className="flex cursor-pointer items-center rounded-2xl border border-pastelPurple bg-lightPastelPurple px-5 py-4">
                    <input
                      type="checkbox"
                      checked={isShippingSame}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setIsShippingSame(checked);

                        if (checked) {
                          setShippingFirstName(firstName || "");
                          setShippingLastName(lastName || "");
                          setShippingAddress(address || "");
                          setShippingCity(city || "");
                          setShippingCountry(country || "");
                          setShippingZip(zip || "");
                          setShippingErrors({});
                        } else {
                          setShippingFirstName("");
                          setShippingLastName("");
                          setShippingAddress("");
                          setShippingCity("");
                          setShippingCountry("");
                          setShippingZip("");
                        }
                      }}
                      className="h-5 w-5 rounded border-pastelPurple text-themePurple focus:ring-themePurple"
                    />
                    <span className="ml-3 font-bold text-darkPurple">
                      Shipping address same as billing address
                    </span>
                  </label>

                  {!isShippingSame && (
                    <div className="mt-7">
                      <h3 className="text-2xl font-extrabold text-darkPurple">
                        Shipping details
                      </h3>

                      <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                          <label className={labelClass}>
                            First Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            className={inputClass(
                              shippingErrors.shippingFirstName
                            )}
                            placeholder="Enter shipping first name"
                            value={shippingFirstName}
                            onChange={(e) => {
                              setShippingFirstName(e.target.value);
                              setShippingErrors({
                                ...shippingErrors,
                                shippingFirstName: false,
                              });
                            }}
                          />
                          {shippingErrors.shippingFirstName && (
                            <p className={errorClass}>
                              Shipping first name is required
                            </p>
                          )}
                        </div>

                        <div>
                          <label className={labelClass}>
                            Last Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            className={inputClass(
                              shippingErrors.shippingLastName
                            )}
                            placeholder="Enter shipping last name"
                            value={shippingLastName}
                            onChange={(e) => {
                              setShippingLastName(e.target.value);
                              setShippingErrors({
                                ...shippingErrors,
                                shippingLastName: false,
                              });
                            }}
                          />
                          {shippingErrors.shippingLastName && (
                            <p className={errorClass}>
                              Shipping last name is required
                            </p>
                          )}
                        </div>

                        <div className="md:col-span-2">
                          <label className={labelClass}>
                            Address <span className="text-red-500">*</span>
                          </label>
                          <input
                            className={inputClass(
                              shippingErrors.shippingAddress
                            )}
                            placeholder="Enter shipping address"
                            value={shippingAddress}
                            onChange={(e) => {
                              setShippingAddress(e.target.value);
                              setShippingErrors({
                                ...shippingErrors,
                                shippingAddress: false,
                              });
                            }}
                          />
                          {shippingErrors.shippingAddress && (
                            <p className={errorClass}>
                              Shipping address is required
                            </p>
                          )}
                        </div>

                        <div>
                          <label className={labelClass}>
                            Town / City{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            className={inputClass(shippingErrors.shippingCity)}
                            placeholder="Enter shipping city"
                            value={shippingCity}
                            onChange={(e) => {
                              setShippingCity(e.target.value);
                              setShippingErrors({
                                ...shippingErrors,
                                shippingCity: false,
                              });
                            }}
                          />
                          {shippingErrors.shippingCity && (
                            <p className={errorClass}>
                              Shipping city is required
                            </p>
                          )}
                        </div>

                        <div>
                          <label className={labelClass}>
                            Country <span className="text-red-500">*</span>
                          </label>
                          <select
                            className={inputClass(
                              shippingErrors.shippingCountry
                            )}
                            value={shippingCountry}
                            onChange={(e) => {
                              setShippingCountry(e.target.value);
                              setShippingErrors({
                                ...shippingErrors,
                                shippingCountry: false,
                              });
                            }}
                          >
                            <option value="">Select a country</option>
                            {countries.map((c) => (
                              <option key={c.code} value={c.name}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                          {shippingErrors.shippingCountry && (
                            <p className={errorClass}>
                              Shipping country is required
                            </p>
                          )}
                        </div>

                        <div>
                          <label className={labelClass}>
                            Postcode / ZIP{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            className={inputClass(shippingErrors.shippingZip)}
                            placeholder="Enter shipping ZIP"
                            value={shippingZip}
                            onChange={(e) => {
                              setShippingZip(e.target.value);
                              setShippingErrors({
                                ...shippingErrors,
                                shippingZip: false,
                              });
                            }}
                          />
                          {shippingErrors.shippingZip && (
                            <p className={errorClass}>
                              Shipping ZIP is required
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <textarea
                className="w-full rounded-[26px] border border-pastelPurple bg-white px-5 py-4 font-medium text-darkPurple shadow-[0_18px_45px_rgba(47,34,84,0.08)] outline-none transition focus:border-themePurple focus:ring-4 focus:ring-pastelPurple/60"
                rows="5"
                placeholder="Order notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <div className="lg:sticky lg:top-24 lg:self-start">
              <div className="overflow-hidden rounded-[30px] border border-pastelPurple bg-white shadow-[0_18px_45px_rgba(47,34,84,0.08)]">
                <div className="bg-darkPurple px-6 py-5">
                  <h3 className="text-2xl font-extrabold text-white">
                    Order Summary
                  </h3>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm text-[#5f5870]">
                    <thead className="bg-lightPastelPurple text-xs font-extrabold uppercase text-darkPurple">
                      <tr>
                        <th className="px-5 py-4">Product</th>
                        <th className="px-5 py-4">Name</th>
                        <th className="px-5 py-4">Unit Price</th>
                        <th className="px-5 py-4">Qty</th>
                        <th className="px-5 py-4">Total</th>
                      </tr>
                    </thead>

                    <tbody>
                      {Array.from(
                        new Map(
                          cartItems.map((item) => [item.product_name, item])
                        ).values()
                      ).map((item) => {
                        const totalPrice = Number(item.price);
                        const unitPrice = item.qty ? totalPrice / item.qty : 0;

                        return (
                          <tr
                            key={item.id}
                            className="border-b border-pastelPurple"
                          >
                            <td className="px-5 py-4">
                              <img
                                src={
                                  item.upload_image
                                    ? `${apiUrl}/storage/${item.upload_image}`
                                    : "../img/no-image.png"
                                }
                                className="h-14 w-14 rounded-2xl border border-pastelPurple object-cover"
                                alt=""
                              />
                            </td>

                            <td className="px-5 py-4 font-bold text-darkPurple">
                              {item.product_name}
                            </td>

                            <td className="px-5 py-4">
                              ${unitPrice.toFixed(2)}
                            </td>

                            <td className="px-5 py-4">{item.qty}</td>

                            <td className="px-5 py-4 font-extrabold text-darkPurple">
                              ${totalPrice.toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="border-b border-pastelPurple bg-lightPastelPurple px-6 py-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <label className="flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        checked={isRushDelivery}
                        onChange={(e) => setIsRushDelivery(e.target.checked)}
                        className="h-5 w-5 rounded border-pastelPurple text-themePurple focus:ring-themePurple"
                      />
                      <span className="ml-3 font-bold text-darkPurple">
                        Rush Delivery (7 days)
                      </span>
                    </label>

                    {isRushDelivery && (
                      <div className="text-sm font-extrabold text-orange-600">
                        +20% Rush Fee
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4 px-6 py-5">
                  <div className="flex justify-between text-base font-medium text-[#5f5870]">
                    <h3>Subtotal</h3>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>

                  <div className="flex justify-between text-base font-medium text-[#5f5870]">
                    <h3>
                      Shipping{" "}
                      {isRushDelivery && (
                        <span className="text-sm text-orange-600">
                          (Rush Delivery)
                        </span>
                      )}
                    </h3>
                    <span>${shippingCharge.toFixed(2)}</span>
                  </div>

                  <div className="flex justify-between rounded-2xl bg-lightPastelPurple px-4 py-3 text-sm font-bold text-darkPurple">
                    Expected Delivery Date:
                    <strong className="text-[#5f7f00]">{deliveryDate}</strong>
                  </div>

                  <div className="border-t border-pastelPurple pt-5">
                    <div className="flex justify-between text-xl font-extrabold text-darkPurple">
                      <h3 className="uppercase">Total</h3>
                      <span>${total.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="pt-3">
                    <StripeCheckout
                      orderData={{
                        first_name: firstName,
                        last_name: lastName,
                        email,
                        total,
                      }}
                      validateForm={validateAllForms}
                      onSuccess={async (paymentId) => {
                        await placeOrder(paymentId, "paid");
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;